import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../theme';

var Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${theme.bgOverlay};
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 24px;
  overflow-y: auto;
`;

var Panel = styled.div`
  width: 100%;
  max-width: 600px;
  background: ${theme.bgGradient};
  border-radius: ${theme.radius};
  border: 1px solid ${theme.border};
  padding: 24px;
  margin: 24px 0;
`;

var FormTitle = styled.h2`
  color: ${theme.accent};
  margin: 0 0 20px 0;
  font-size: 1.2rem;
`;

var Label = styled.label`
  display: block;
  color: ${theme.textMuted};
  font-size: 0.8rem;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

var Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: ${theme.bg};
  color: #fff;
  font-size: 1rem;
  margin-bottom: 12px;
  &:focus { outline: none; border-color: ${theme.accent}; }
  &::placeholder { color: ${theme.textPlaceholder}; }
`;

var TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: ${theme.bg};
  color: #fff;
  font-size: 1rem;
  margin-bottom: 12px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  &:focus { outline: none; border-color: ${theme.accent}; }
`;

var Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: ${theme.bg};
  color: #fff;
  font-size: 1rem;
  margin-bottom: 12px;
  &:focus { outline: none; border-color: ${theme.accent}; }
`;

var Row = styled.div`
  display: flex;
  gap: 12px;
  & > * { flex: 1; }
`;

var IngRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

var SmallInput = styled.input`
  padding: 8px;
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: ${theme.bg};
  color: #fff;
  font-size: 0.85rem;
  &:focus { outline: none; border-color: ${theme.accent}; }
`;

var AddBtn = styled.button`
  padding: 8px 16px;
  border: 1px dashed ${theme.accent};
  border-radius: ${theme.radiusSm};
  background: transparent;
  color: ${theme.accent};
  cursor: pointer;
  font-size: 0.85rem;
  margin-bottom: 12px;
  &:hover { background: rgba(74,222,128,0.1); }
`;

var RemBtn = styled.button`
  background: none;
  border: none;
  color: ${theme.error};
  cursor: pointer;
  font-size: 1rem;
  padding: 4px 8px;
  &:hover { opacity: 0.7; }
`;

var BtnRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

var SaveBtn = styled.button`
  flex: 1;
  padding: 14px;
  border: none;
  border-radius: ${theme.radiusSm};
  background: ${theme.accentGradient};
  color: #000;
  font-weight: 700;
  cursor: pointer;
  min-height: 48px;
`;

var CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: transparent;
  color: ${theme.textMuted};
  cursor: pointer;
  min-height: 48px;
  &:hover { border-color: ${theme.error}; }
`;

var CATEGORIES = ['breakfast', 'lunch', 'dinner', 'snack', 'dessert', 'side'];

var RecipeForm = function ({ recipe, onSave, onClose }) {
  var [title, setTitle] = useState(recipe?.title || '');
  var [description, setDescription] = useState(recipe?.description || '');
  var [category, setCategory] = useState(recipe?.category || 'dinner');
  var [servings, setServings] = useState(recipe?.servings || 4);
  var [prepTime, setPrepTime] = useState(recipe?.prepTime || 0);
  var [cookTime, setCookTime] = useState(recipe?.cookTime || 0);
  var [tags, setTags] = useState(recipe?.tags?.join(', ') || '');
  var [ingredients, setIngredients] = useState(recipe?.ingredients || []);
  var [instructions, setInstructions] = useState(recipe?.instructions?.join('\n') || '');
  var [notes, setNotes] = useState(recipe?.notes || '');

  var addIngredient = function () {
    setIngredients([...ingredients, { name: '', amount: '', unit: '', category: 'other' }]);
  };

  var updateIngredient = function (i, field, val) {
    var updated = [...ingredients];
    updated[i] = { ...updated[i], [field]: val };
    setIngredients(updated);
  };

  var removeIngredient = function (i) {
    setIngredients(ingredients.filter(function (_, idx) { return idx !== i; }));
  };

  var handleSubmit = function (e) {
    e.preventDefault();
    onSave({
      title: title.trim(),
      description: description.trim(),
      category,
      servings: parseInt(servings) || 4,
      prepTime: parseInt(prepTime) || 0,
      cookTime: parseInt(cookTime) || 0,
      tags: tags.split(',').map(function (t) { return t.trim(); }).filter(Boolean),
      ingredients: ingredients.filter(function (ing) { return ing.name.trim(); }),
      instructions: instructions.split('\n').map(function (s) { return s.trim(); }).filter(Boolean),
      notes: notes.trim()
    });
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={function (e) { e.stopPropagation(); }}>
        <FormTitle>{recipe ? 'Edit Recipe' : 'New Recipe'}</FormTitle>
        <form onSubmit={handleSubmit}>
          <Label>Title</Label>
          <Input value={title} onChange={function (e) { setTitle(e.target.value); }} placeholder="Recipe name" required />

          <Label>Category</Label>
          <Select value={category} onChange={function (e) { setCategory(e.target.value); }}>
            {CATEGORIES.map(function (c) { return <option key={c} value={c}>{c}</option>; })}
          </Select>

          <Label>Description</Label>
          <TextArea value={description} onChange={function (e) { setDescription(e.target.value); }} placeholder="Brief description..." />

          <Row>
            <div>
              <Label>Servings</Label>
              <Input type="number" value={servings} onChange={function (e) { setServings(e.target.value); }} min="1" />
            </div>
            <div>
              <Label>Prep (min)</Label>
              <Input type="number" value={prepTime} onChange={function (e) { setPrepTime(e.target.value); }} min="0" />
            </div>
            <div>
              <Label>Cook (min)</Label>
              <Input type="number" value={cookTime} onChange={function (e) { setCookTime(e.target.value); }} min="0" />
            </div>
          </Row>

          <Label>Tags (comma separated)</Label>
          <Input value={tags} onChange={function (e) { setTags(e.target.value); }} placeholder="quick, healthy, vegetarian" />

          <Label>Ingredients</Label>
          {ingredients.map(function (ing, i) {
            return (
              <IngRow key={i}>
                <SmallInput style={{ width: '60px' }} value={ing.amount} onChange={function (e) { updateIngredient(i, 'amount', e.target.value); }} placeholder="Amt" />
                <SmallInput style={{ width: '60px' }} value={ing.unit} onChange={function (e) { updateIngredient(i, 'unit', e.target.value); }} placeholder="Unit" />
                <SmallInput style={{ flex: 1 }} value={ing.name} onChange={function (e) { updateIngredient(i, 'name', e.target.value); }} placeholder="Ingredient name" />
                <RemBtn type="button" onClick={function () { removeIngredient(i); }}>✕</RemBtn>
              </IngRow>
            );
          })}
          <AddBtn type="button" onClick={addIngredient}>+ Add Ingredient</AddBtn>

          <Label>Instructions (one step per line)</Label>
          <TextArea value={instructions} onChange={function (e) { setInstructions(e.target.value); }} placeholder="Step 1...&#10;Step 2..." style={{ minHeight: '120px' }} />

          <Label>Notes</Label>
          <TextArea value={notes} onChange={function (e) { setNotes(e.target.value); }} placeholder="Tips, variations..." />

          <BtnRow>
            <CancelBtn type="button" onClick={onClose}>Cancel</CancelBtn>
            <SaveBtn type="submit">{recipe ? 'Update' : 'Create'} Recipe</SaveBtn>
          </BtnRow>
        </form>
      </Panel>
    </Overlay>
  );
};

export default RecipeForm;
